import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
    PageHeader, Card, LoadingState, EmptyState, ErrorState, Pill,
    CopperButton, SlateButton, Modal, Field, TextInput, NumberInput, Select,
} from "@/components/common/UI";
import { useAuth, hasRole } from "@/lib/auth";
import { Scissors, Plus, Flame, ArrowUp, Trash2 } from "lucide-react";
import { fmtINR, fmtIN } from "@/lib/format";
import { toast } from "sonner";
import TID from "@/constants/testIds";
import { ImportExportBar } from "@/components/foundation/ImportExportBar";

export default function ProcessRates() {
    const { user } = useAuth();
    const [rows, setRows] = useState(null);
    const [error, setError] = useState("");
    const [tab, setTab] = useState("CUTTING");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({
        process_type: "CUTTING", thickness_or_fillet: 0, rate: 0, description: "",
        customer_tag: "", effective_date: new Date().toISOString().slice(0, 10), status: "APPROVED",
    });
    const [submitting, setSubmitting] = useState(false);
    const canEdit = hasRole(user, "CORPORATE_ADMIN");

    const load = () =>
        api.get("/process-rates").then((r) => setRows(r.data)).catch((e) => setError(formatApiError(e)));
    useEffect(() => { load(); }, []);

    const filtered = rows?.filter((r) => r.process_type === tab) || [];

    const openCreate = () => {
        setEditing(null);
        setForm({
            process_type: tab, thickness_or_fillet: 0, rate: 0, description: "",
            customer_tag: "", effective_date: new Date().toISOString().slice(0, 10), status: "APPROVED",
            volume_min: "", volume_max: "",
        });
        setOpen(true);
    };
    const openEdit = (r) => {
        setEditing(r);
        setForm({
            process_type: r.process_type, thickness_or_fillet: r.thickness_or_fillet,
            rate: r.rate, description: r.description || "",
            customer_tag: r.customer_tag || "",
            effective_date: r.effective_date ? r.effective_date.slice(0, 10) : new Date().toISOString().slice(0, 10),
            status: r.status || "APPROVED",
            volume_min: r.volume_min ?? "",
            volume_max: r.volume_max ?? "",
        });
        setOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                thickness_or_fillet: parseFloat(form.thickness_or_fillet),
                rate: parseFloat(form.rate),
                volume_min: form.volume_min === "" || form.volume_min === null ? null : parseInt(form.volume_min, 10),
                volume_max: form.volume_max === "" || form.volume_max === null ? null : parseInt(form.volume_max, 10),
            };
            if (editing) await api.put(`/process-rates/${editing.id}`, payload);
            else await api.post("/process-rates", payload);
            toast.success(editing ? "Rate updated" : "Rate added");
            setOpen(false);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSubmitting(false); }
    };

    const remove = async (row) => {
        if (!window.confirm(`Delete this ${row.process_type.toLowerCase()} rate (${row.thickness_or_fillet}mm)? This action cannot be undone.`)) return;
        try {
            await api.delete(`/process-rates/${row.id}`);
            toast.success("Process rate deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div data-testid={TID.processRatesPage}>
            <PageHeader
                eyebrow="FOUNDATION · LAYER 5"
                title="Process Rates"
                subtitle="Cutting (₹/mtr/mm) and Welding (₹/mtr by fillet size). Welding rates always round UP: a 4.2mm fillet uses the 5mm rate."
                actions={
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                        <ImportExportBar entity="process-rates" label="Process Rates" canImport={canEdit} onImported={load} />
                        {canEdit && (
                            <CopperButton onClick={openCreate} data-testid={TID.processRatesAddBtn}>
                                <Plus className="h-4 w-4" /> Add Rate
                            </CopperButton>
                        )}
                    </div>
                }
            />

            {/* Tabs */}
            <div className="border-b border-slate-200 mb-5 flex gap-1">
                {[
                    { key: "CUTTING", label: "Cutting", icon: Scissors, hint: "₹ per metre per mm thickness" },
                    { key: "WELDING", label: "Welding", icon: Flame, hint: "₹ per metre · round UP fillet size" },
                ].map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.key;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={[
                                "relative -mb-px px-4 py-2.5 text-sm font-medium flex items-center gap-2 transition-colors",
                                active ? "text-[hsl(var(--copper))] border-b-2 border-[hsl(var(--copper))]" : "text-slate-500 hover:text-slate-700",
                            ].join(" ")}
                        >
                            <Icon className="h-4 w-4" /> {t.label}
                            <span className="text-[10px] text-slate-400 hidden sm:inline">· {t.hint}</span>
                        </button>
                    );
                })}
            </div>

            {error && <ErrorState message={error} onRetry={load} />}
            {!error && rows === null && <LoadingState />}
            {rows && filtered.length === 0 && <EmptyState icon={Scissors} title={`No ${tab.toLowerCase()} rates yet`} />}

            {filtered.length > 0 && (
                <Card>
                    <div className="overflow-x-auto">
                    <table data-testid={TID.processRatesTable} className="w-full table-rowhover">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50/60">
                                <th className="eyebrow text-right py-2 px-4">{tab === "CUTTING" ? "Thickness (mm)" : "Fillet Size (mm)"}</th>
                                <th className="eyebrow text-right py-2 px-4">Rate ({tab === "CUTTING" ? "₹/mtr/mm" : "₹/mtr"})</th>
                                <th className="eyebrow text-left py-2 px-4">Volume Tier</th>
                                <th className="eyebrow text-left py-2 px-4">Description</th>
                                <th className="eyebrow text-left py-2 px-4">Status</th>
                                <th className="eyebrow text-right py-2 px-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => (
                                <tr key={r.id} className="border-b border-slate-100">
                                    <td className="py-1.5 px-4 text-right font-mono tabular-nums font-semibold text-[13px]">
                                        {fmtIN(r.thickness_or_fillet, { decimals: 1 })} mm
                                    </td>
                                    <td className="py-1.5 px-4 text-right font-mono tabular-nums text-[13px] font-semibold">{fmtINR(r.rate)}</td>
                                    <td className="py-1.5 px-4 text-[12px] text-slate-600 font-mono">
                                        {r.volume_min == null && r.volume_max == null ? (
                                            <span className="text-slate-400">All</span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--copper))] bg-[hsl(var(--copper-soft))]/40 border border-[hsl(var(--copper))]/30 px-1.5 py-0.5 rounded">
                                                {r.volume_min ?? "—"} – {r.volume_max ?? "∞"}
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-1.5 px-4 text-[12px] text-slate-600">{r.description || "—"}</td>
                                    <td className="py-1.5 px-4"><Pill tone={r.is_active ? "emerald" : "slate"}>{r.is_active ? "Active" : "Inactive"}</Pill></td>
                                    <td className="py-1.5 px-4 text-right">
                                        {canEdit && (
                                            <div className="inline-flex gap-1">
                                                <SlateButton onClick={() => openEdit(r)} className="h-7 px-2.5 text-xs">Edit</SlateButton>
                                                <SlateButton
                                                    onClick={() => remove(r)}
                                                    variant="ghost"
                                                    className="h-7 px-2 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                                                    title="Delete this rate"
                                                    data-testid={`process-rate-delete-${r.id}`}
                                                >
                                                    <Trash2 className="h-3 w-3" />
                                                </SlateButton>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                </Card>
            )}

            {tab === "WELDING" && (
                <Card className="mt-4 p-3 border-amber-200 bg-amber-50">
                    <div className="flex items-start gap-2 text-[12px] text-amber-900">
                        <ArrowUp className="h-3.5 w-3.5 text-amber-700 mt-0.5 shrink-0" />
                        <div>
                            <span className="font-semibold">Rule 4 · Round UP</span> — When a part&apos;s calculated fillet is between two listed sizes (e.g. 4.2 mm), the ZBC will always pick the <span className="font-semibold">larger</span> rate (5 mm). This is non-negotiable: never under-cost a weld.
                        </div>
                    </div>
                </Card>
            )}

            <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Process Rate" : `Add ${form.process_type} Rate`} size="md"
                footer={<>
                    <SlateButton variant="outline" onClick={() => setOpen(false)}>Cancel</SlateButton>
                    <CopperButton onClick={submit} disabled={submitting}>{submitting ? "Saving…" : "Save"}</CopperButton>
                </>}>
                <form onSubmit={submit} className="space-y-5">
                    <Field label="Process Type" required>
                        <Select value={form.process_type} onChange={(e) => setForm({ ...form, process_type: e.target.value })} disabled={!!editing}>
                            <option value="CUTTING">Cutting</option>
                            <option value="WELDING">Welding</option>
                        </Select>
                    </Field>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label={form.process_type === "CUTTING" ? "Thickness (mm)" : "Fillet Size (mm)"} required>
                            <NumberInput value={form.thickness_or_fillet} onChange={(e) => setForm({ ...form, thickness_or_fillet: e.target.value })} step="0.1" required />
                        </Field>
                        <Field label={form.process_type === "CUTTING" ? "Rate (₹/mtr/mm)" : "Rate (₹/mtr)"} required>
                            <NumberInput value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} step="0.01" required />
                        </Field>
                    </div>
                    <Field label="Description">
                        <TextInput value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g., Laser cut ≤ 4mm" />
                    </Field>
                    <div className="grid grid-cols-2 gap-4">
                        <Field
                            label="Volume Min (pcs/month)"
                            helper="Blank = no lower bound. This rate applies from this volume upward."
                        >
                            <NumberInput
                                value={form.volume_min}
                                onChange={(e) => setForm({ ...form, volume_min: e.target.value })}
                                step="1" min="0"
                                placeholder="e.g., 100"
                                data-testid="process-rate-volume-min"
                            />
                        </Field>
                        <Field
                            label="Volume Max (pcs/month)"
                            helper="Blank = no upper bound. Exclusive upper limit."
                        >
                            <NumberInput
                                value={form.volume_max}
                                onChange={(e) => setForm({ ...form, volume_max: e.target.value })}
                                step="1" min="0"
                                placeholder="e.g., 1000"
                                data-testid="process-rate-volume-max"
                            />
                        </Field>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                        <Field label="Customer Tag" helper="Blank = all customers; routes to customer-specific rate">
                            <TextInput value={form.customer_tag} onChange={(e) => setForm({ ...form, customer_tag: e.target.value })} placeholder="e.g., Volvo CE India" data-testid="process-rate-customer-tag" />
                        </Field>
                        <Field label="Effective Date">
                            <TextInput type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} data-testid="process-rate-effective-date" />
                        </Field>
                        <Field label="Status">
                            <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="process-rate-status">
                                <option value="APPROVED">Approved</option>
                                <option value="PENDING_CONFIRMATION">Pending Confirmation</option>
                                <option value="DRAFT">Draft</option>
                            </Select>
                        </Field>
                    </div>
                </form>
            </Modal>
        </div>
    );
}